import { useMemo } from 'react';
import { Card, Center, Stack, Text } from '@mantine/core';
import type {
  CoachCohortDetail,
  CoachCohortGroup,
  CoachCohortParticipant,
  CoachGroupInfo,
} from '../../../services/api/coachPortal';
import type { GroupWithMembers } from '../../../services/api/groups';
import {
  GroupCard,
  type GroupCardParticipant,
} from '../../cohorts/components/GroupCard';
import {
  formatGroupSchedule,
  type GroupCardSession,
} from '../../cohorts/components/groups/groupUtils';
import { useAuthStore } from '../../../stores/auth';
import { formatTime } from '../../../utils/formatters';
import { getDisplayTimezone } from '../../../utils/timezone';

/**
 * Map a coach group to the GroupCard shape. GroupCard reads the fields below;
 * the CM-only fields (coach, capacity, matching sync) get neutral values
 * because the read-only card never shows them.
 */
function toGroupWithMembers(
  group: CoachCohortGroup,
  cohortId: string,
  timeSlot: CoachGroupInfo['time_slot'] | undefined
): GroupWithMembers {
  return {
    id: group.id,
    cohort_id: cohortId,
    name: group.name,
    description: null,
    max_size: group.member_count,
    coach_id: null,
    is_active: true,
    member_count: group.member_count,
    avg_engagement: group.avg_engagement,
    attendance_percentage: group.attendance_percentage,
    attendance_marked: group.attendance_marked,
    attendance_population: group.attendance_population,
    scheduled_time_slot_id: null,
    time_slot_day: timeSlot?.day_of_week,
    time_slot_time: timeSlot?.time,
    time_slot_timezone: timeSlot?.timezone,
    synced_by_matching_session_id: null,
    created_at: '',
    updated_at: '',
    // users.first_name and last_name can be null; never render "null".
    members: group.members.map(member => ({
      ...member,
      first_name: member.first_name ?? '',
      last_name: member.last_name ?? '',
      joined_at: '',
    })),
  };
}

/**
 * Map the coach's session list to GroupCard rows. Dates show in the coach's
 * own timezone, the same as the coach Sessions tab.
 */
function toGroupCardSessions(
  sessions: CoachGroupInfo['sessions'],
  coachName: string | null
): GroupCardSession[] {
  const timeZone = getDisplayTimezone();
  const now = Date.now();
  return [...sessions]
    .sort((a, b) => (a.session_number || 0) - (b.session_number || 0))
    .map((session, index) => {
      const num = session.session_number || index + 1;
      return {
        id: session.id,
        number: num,
        // MED-1367: fall back to the session title before "(no topic)".
        topic:
          session.topic_name || session.title || `Session ${num} (no topic)`,
        date: new Date(session.scheduled_start).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          timeZone,
        }),
        time: formatTime(session.scheduled_start, timeZone),
        coachName,
        status: session.status,
        // MED-1367: a session is past once it starts, or once it completes.
        isPast:
          session.status === 'completed' ||
          new Date(session.scheduled_start).getTime() < now,
      };
    });
}

interface CoachGroupsTabContentProps {
  cohort: CoachCohortDetail;
  groups: CoachCohortGroup[];
  participants: CoachCohortParticipant[];
  // MED-1367: opens the coach session details drawer, the same as the
  // Sessions tab.
  onSessionClick?: (sessionId: string) => void;
}

/**
 * MED-1367: the coach's groups as read-only cards. Reuses the CM GroupCard
 * with readOnly, so stats, members and sessions look the same as the CM view.
 */
export function CoachGroupsTabContent({
  cohort,
  groups,
  participants,
  onSessionClick,
}: CoachGroupsTabContentProps) {
  const user = useAuthStore(state => state.user);
  const coachName = user
    ? `${user.first_name} ${user.last_name}`.trim() || null
    : null;

  // Status per member, so withdrawn members fade and carry the WITHDRAWN
  // pill, the same as the CM card. Flags and demographics stay empty.
  const participantMap = useMemo(() => {
    const map: Record<string, GroupCardParticipant> = {};
    for (const p of participants) {
      map[p.user_id] = {
        id: p.id,
        status: p.status,
        withdrawn_at: p.withdrawn_at,
        has_open_flag: false,
        flag_id: null,
        personality_type: null,
        custom_fields: null,
      };
    }
    return map;
  }, [participants]);

  const cards = useMemo(() => {
    const infoById = new Map(cohort.groups.map(g => [g.id, g]));
    return groups.map(group => {
      const info = infoById.get(group.id);
      const groupWithMembers = toGroupWithMembers(
        group,
        cohort.id,
        info?.time_slot
      );
      return {
        group: groupWithMembers,
        schedule: formatGroupSchedule(groupWithMembers),
        sessions: toGroupCardSessions(info?.sessions ?? [], coachName),
      };
    });
  }, [cohort, groups, coachName]);

  if (cards.length === 0) {
    return (
      <Card withBorder p="xl">
        <Center>
          <Text c="dimmed" size="sm">
            You do not lead a group in this cohort yet.
          </Text>
        </Center>
      </Card>
    );
  }

  return (
    <Stack gap="md">
      {cards.map(({ group, schedule, sessions }, index) => (
        <GroupCard
          key={group.id}
          group={group}
          cohortId={cohort.id}
          colorIndex={index}
          schedule={schedule}
          sessions={sessions}
          configuredSessionCount={cohort.group_session_count}
          participantMap={participantMap}
          readOnly
          onSessionClick={onSessionClick}
        />
      ))}
    </Stack>
  );
}
